import { useEffect, useRef } from 'react';

const BOX_CLASS = 'viewport-info-box';
const STYLE_ELEMENT_ID = 'viewport-info-box-style';

const CSS = `
.${BOX_CLASS} {
    position: absolute;
    top: 80px;
    left: 8px;
    background: rgba(35, 35, 35, 0.85);
    border-radius: 4px;
    padding: 8px 20px;
    color: white;
    font-family: monospace;
    font-size: 12px;
    line-height: 18px;
    pointer-events: none;
    user-select: none;
    z-index: 1;
    min-width: 0;
    width: fit-content;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.6);
}
.${BOX_CLASS} .title {
    color: #ffffff;
    font-size: 14px;
    font-weight: normal;
    letter-spacing: 0.5px;
    text-align: center;
    margin-bottom: 8px;
    opacity: 1;
}
.${BOX_CLASS} .row {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    font-size: 11px;
}
.${BOX_CLASS} .row .label { color: #cccccc; }
.${BOX_CLASS} .row .value { color: #ffffff; }
`;

function ensureStyleInjected() {
    if (document.getElementById(STYLE_ELEMENT_ID)) return;
    const styleEl = document.createElement('style');
    styleEl.id = STYLE_ELEMENT_ID;
    styleEl.textContent = CSS;
    document.head.appendChild(styleEl);
}

function buildBoxNode(title, rows) {
    const root = document.createElement('div');
    root.classList.add(BOX_CLASS);

    const titleEl = document.createElement('div');
    titleEl.classList.add('title');
    titleEl.textContent = title;
    root.appendChild(titleEl);

    const valueEls = rows.map((r) => {
        const rowEl = document.createElement('div');
        rowEl.classList.add('row');
        const labelEl = document.createElement('span');
        labelEl.classList.add('label');
        labelEl.textContent = r.label;
        const valueEl = document.createElement('span');
        valueEl.classList.add('value');
        rowEl.appendChild(labelEl);
        rowEl.appendChild(valueEl);
        root.appendChild(rowEl);
        return valueEl;
    });

    return { root, valueEls };
}

function updateValues(nodes, rows) {
    rows.forEach((r, i) => {
        const el = nodes.valueEls[i];
        if (!el) return;
        el.textContent = r.value;
        el.style.color = r.color || '';
    });
}

export function useViewportInfoBox(containerRef, initialized, isVisible, { title, rows }) {
    const context = useRef(null);
    const container = containerRef.current;

    useEffect(() => {
        if (container && !context.current) {
            ensureStyleInjected();
            const nodes = buildBoxNode(title, rows);
            container.appendChild(nodes.root);
            updateValues(nodes, rows);
            context.current = nodes;
        }
        let ctx = context.current;
        return () => {
            if (ctx) {
                if (ctx.root.parentNode === container) container.removeChild(ctx.root);
                context.current = null;
                ctx = null;
            }
        };
    }, [container]);

    useEffect(() => {
        if (context.current) {
            context.current.root.style.visibility = initialized && isVisible ? 'inherit' : 'hidden';
        }
    }, [isVisible, initialized, container]);

    useEffect(() => {
        if (context.current) {
            updateValues(context.current, rows);
        }
    }, [rows]);
}
